import { useEffect, useRef } from 'react';

import {
  CALENDAR_EVENT_LABELS,
  IEvent,
  IGuest
} from 'components/Calendar/interface';
import { notify } from 'components/Notifications/HotToastNotifications';
import {
  getNotificationAudio,
  NotificationAudio
} from 'components/Notifications/notificationSound';
import {
  buildDashboardLink,
  buildDealLink
} from 'components/Notifications/ChatterNotificationsListener/helpers';
import { firebase } from 'redux/firebase';
import { useTypedSelector } from 'redux/reducers';

type CalendarEvent = IEvent & {
  id: string;
};

const hasToDate = (
  value: unknown
): value is {
  toDate: () => Date;
} => {
  return (
    !!value &&
    typeof value === 'object' &&
    'toDate' in value &&
    typeof (value as { toDate?: unknown }).toDate === 'function'
  );
};

const toDate = (value: unknown): Date | null => {
  if (value instanceof Date) return value;

  if (hasToDate(value)) {
    return value.toDate();
  }

  if (typeof value === 'string' || typeof value === 'number') {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  return null;
};

const isToday = (date: Date, now: Date) => {
  return (
    date.getDate() === now.getDate() &&
    date.getMonth() === now.getMonth() &&
    date.getFullYear() === now.getFullYear()
  );
};

const isVisibleInvitedEvent = (event: CalendarEvent, userId: number) => {
  const guest = event.extendedProps?.guests?.find(
    (item: IGuest) => item.UserInstanceId === userId
  );

  return guest ? guest.status !== 2 : false;
};

const formatEventTime = (eventTime: number) => {
  return new Date(eventTime)
    .toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    })
    .replace(' ', '');
};

export const CalendarNotificationsListener = () => {
  const userId = useTypedSelector((s) => s.user.user?.Id);

  const ownedEventsRef = useRef<Map<string, CalendarEvent>>(new Map());
  const invitedEventsRef = useRef<Map<string, CalendarEvent>>(new Map());
  const scheduledTimeoutsRef = useRef<Map<string, number>>(new Map());
  const dispatchedNotificationsRef = useRef<Set<string>>(new Set());
  const notificationAudioRef = useRef<NotificationAudio | null>(null);
  const restoreTitleRef = useRef<string | null>(null);
  const pendingTitleCountRef = useRef(0);

  useEffect(() => {
    if (!userId) {
      ownedEventsRef.current.clear();
      invitedEventsRef.current.clear();
      dispatchedNotificationsRef.current.clear();
      notificationAudioRef.current = null;
      return;
    }

    const clearScheduledNotifications = () => {
      scheduledTimeoutsRef.current.forEach((timeoutId) => {
        window.clearTimeout(timeoutId);
      });
      scheduledTimeoutsRef.current.clear();
    };

    const resetDocumentTitle = () => {
      pendingTitleCountRef.current = 0;

      if (restoreTitleRef.current) {
        document.title = restoreTitleRef.current;
        restoreTitleRef.current = null;
      }
    };

    const reconcileScheduledNotifications = async () => {
      const audio =
        notificationAudioRef.current ?? (await getNotificationAudio(userId));
      notificationAudioRef.current = audio;

      const now = new Date();
      const validNotificationKeys = new Set<string>();
      const immediateNotifications = new Map<
        string,
        { eventTime: number; event: CalendarEvent }
      >();
      const nextSchedules = new Map<
        string,
        { eventTime: number; event: CalendarEvent }
      >();

      const relevantEvents = new Map<string, CalendarEvent>();

      ownedEventsRef.current.forEach((event, id) => {
        relevantEvents.set(id, event);
      });

      invitedEventsRef.current.forEach((event, id) => {
        if (!relevantEvents.has(id)) {
          relevantEvents.set(id, event);
        }
      });

      relevantEvents.forEach((event) => {
        if (!event.extendedProps?.notification || !event.id) return;

        const eventStart = toDate(event.start);
        if (!eventStart || !isToday(eventStart, now)) return;

        const eventTime = eventStart.getTime();
        if (eventTime <= now.getTime()) return;

        const notificationKey = `${event.id}:${eventTime}`;
        validNotificationKeys.add(notificationKey);

        const notificationTriggerTime = eventTime - audio.leadTimeMs;
        const delay = notificationTriggerTime - now.getTime();

        if (delay <= 0) {
          immediateNotifications.set(notificationKey, {
            eventTime,
            event
          });
          return;
        }

        nextSchedules.set(notificationKey, {
          eventTime,
          event
        });
      });

      dispatchedNotificationsRef.current.forEach((key) => {
        if (!validNotificationKeys.has(key)) {
          dispatchedNotificationsRef.current.delete(key);
        }
      });

      scheduledTimeoutsRef.current.forEach((timeoutId, key) => {
        if (!nextSchedules.has(key)) {
          window.clearTimeout(timeoutId);
          scheduledTimeoutsRef.current.delete(key);
        }
      });

      const showNotification = (
        key: string,
        eventTime: number,
        event: CalendarEvent
      ) => {
        if (dispatchedNotificationsRef.current.has(key)) return;

        dispatchedNotificationsRef.current.add(key);
        notificationAudioRef.current?.play();

        const eventTypeLabel =
          CALENDAR_EVENT_LABELS[event.extendedProps.eventType] ||
          event.extendedProps.eventType;
        const title = `🔴 ${eventTypeLabel} — ${event.title} at ${formatEventTime(eventTime)}`;
        const dealInformation = event.extendedProps.dealInformation;
        const actionLinks: Array<{
          href: string;
          label: string;
          color?: string;
        }> = [];

        if (
          dealInformation?.dealName &&
          dealInformation.piid &&
          dealInformation.pdid &&
          dealInformation.psdid
        ) {
          actionLinks.push({
            href: buildDealLink({
              dealId: dealInformation.piid,
              dealDefId: dealInformation.pdid,
              dealName: dealInformation.dealName,
              dealStepDefId: dealInformation.psdid
            }),
            label: `Deal: ${dealInformation.dealName}`,
            color: '#1976d2'
          });
        }

        actionLinks.push({
          href: buildDashboardLink(),
          label: 'View Global Calendar',
          color: '#1976d2'
        });

        if (document.hidden) {
          if (pendingTitleCountRef.current === 0) {
            restoreTitleRef.current = document.title;
          }

          pendingTitleCountRef.current += 1;
          document.title = `🔴 Upcoming Event - ${event.title}`;
        }

        notify.warning('', {
          actionLinks,
          duration: Infinity,
          title,
          toastId: `calendar-${key}`
        });
      };

      immediateNotifications.forEach(({ eventTime, event }, key) => {
        showNotification(key, eventTime, event);
      });

      nextSchedules.forEach(({ eventTime, event }, key) => {
        if (
          scheduledTimeoutsRef.current.has(key) ||
          dispatchedNotificationsRef.current.has(key)
        ) {
          return;
        }

        const delay = eventTime - audio.leadTimeMs - Date.now();
        if (delay <= 0) return;

        const timeoutId = window.setTimeout(() => {
          scheduledTimeoutsRef.current.delete(key);
          showNotification(key, eventTime, event);
        }, delay);

        scheduledTimeoutsRef.current.set(key, timeoutId);
      });
    };

    const eventsRef = firebase.firestore().collection('calendar');
    const ownedEventsQuery = eventsRef.where(
      'extendedProps.calendarOwner',
      '==',
      userId
    );
    const invitedEventsQuery = eventsRef.where(
      'extendedProps.invitedUserInstanceIds',
      'array-contains',
      userId
    );

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        resetDocumentTitle();
      }
    };

    const unsubscribeOwned = ownedEventsQuery.onSnapshot((snapshot) => {
      const nextOwnedEvents = new Map<string, CalendarEvent>();

      snapshot.docs.forEach((doc) => {
        nextOwnedEvents.set(doc.id, {
          id: doc.id,
          ...(doc.data() as IEvent)
        });
      });

      ownedEventsRef.current = nextOwnedEvents;
      void reconcileScheduledNotifications();
    });

    const unsubscribeInvited = invitedEventsQuery.onSnapshot((snapshot) => {
      const nextInvitedEvents = new Map<string, CalendarEvent>();

      snapshot.docs.forEach((doc) => {
        const event = {
          id: doc.id,
          ...(doc.data() as IEvent)
        };

        if (isVisibleInvitedEvent(event, userId)) {
          nextInvitedEvents.set(doc.id, event);
        }
      });

      invitedEventsRef.current = nextInvitedEvents;
      void reconcileScheduledNotifications();
    });

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      unsubscribeOwned();
      unsubscribeInvited();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearScheduledNotifications();
      resetDocumentTitle();
      ownedEventsRef.current.clear();
      invitedEventsRef.current.clear();
      dispatchedNotificationsRef.current.clear();
      notificationAudioRef.current = null;
    };
  }, [userId]);

  return null;
};

export default CalendarNotificationsListener;
